/** One WebSocket per tab to /ws, reconnecting with backoff. Listeners get every server event. */

export type ServerEvent = { type: string; [key: string]: any };
type Listener = (event: ServerEvent) => void;

const listeners = new Set<Listener>();
let socket: WebSocket | null = null;
let retry = 0;
let timer: number | undefined;
let pingTimer: number | undefined;
let stopped = true;

export type ConnectionState = "connecting" | "open" | "closed";
let state: ConnectionState = "closed";
const stateListeners = new Set<(s: ConnectionState) => void>();

function setState(s: ConnectionState) {
  state = s;
  stateListeners.forEach((l) => l(s));
}

function emit(event: ServerEvent) {
  listeners.forEach((l) => {
    try {
      l(event);
    } catch (e) {
      console.error("event listener failed", e);
    }
  });
}

const CLIENT_KEY = "home-ai-client-id";
let memoryClientId: string | null = null;

/** Identifies this browser to the server (tabs share it, like they share temporary chats): temporary
 * chats' events only go to the browser that started them. Not a secret. */
export function clientId(): string {
  try {
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    return (memoryClientId ??= randomId());
  }
}

function randomId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");
}

function connect() {
  if (stopped) return;
  const url = `${location.protocol === "https:" ? "wss" : "ws"}://${location.host}/ws?client=${clientId()}`;
  setState("connecting");
  const ws = new WebSocket(url);
  socket = ws;
  ws.onopen = () => {
    const reconnect = retry > 0;
    retry = 0;
    setState("open");
    if (reconnect) emit({ type: "reconnected" });
    window.clearInterval(pingTimer);
    pingTimer = window.setInterval(() => ws.readyState === WebSocket.OPEN && ws.send(JSON.stringify({ type: "ping" })), 25_000);
  };
  ws.onmessage = (msg) => {
    try {
      emit(JSON.parse(msg.data));
    } catch {
      /* ignore malformed */
    }
  };
  ws.onclose = (ev) => {
    window.clearInterval(pingTimer);
    if (socket !== ws) return;
    socket = null;
    setState("closed");
    if (ev.code === 4401) {
      emit({ type: "unauthorized" });
      return;
    }
    retry += 1;
    const delay = Math.min(30_000, 500 * 2 ** Math.min(retry, 6));
    timer = window.setTimeout(connect, delay);
  };
}

export function startEvents() {
  if (!stopped) return;
  stopped = false;
  retry = 0;
  connect();
}

export function stopEvents() {
  stopped = true;
  window.clearTimeout(timer);
  socket?.close();
  socket = null;
}

export function onEvent(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onConnectionState(listener: (s: ConnectionState) => void): () => void {
  stateListeners.add(listener);
  listener(state);
  return () => stateListeners.delete(listener);
}

// Reconnect at once when the tab comes back or the network returns.
window.addEventListener("online", () => {
  if (!stopped && !socket) {
    window.clearTimeout(timer);
    connect();
  }
});
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && !stopped && !socket) {
    window.clearTimeout(timer);
    connect();
  }
});
